/**
 * Which columns a table hides, remembered per table (P30).
 *
 * =========================================================================
 * WHY THIS IS THE ONLY THING BESIDE WIDTHS THAT GETS localStorage
 * =========================================================================
 * P22's rule is that the URL is the state, and P30 keeps it: sort and every
 * filter go in the query string, so a board can be shared. Column widths were
 * always the exception (`useResizableColumns`), for a reason that applies here
 * word for word — a hidden column is a property of this person's screen, not of
 * the selection they are looking at. Sending "I hid Doc Type" to a colleague
 * along with "the dch tickets nobody has identified" would be sending them
 * furniture, not information.
 *
 * The other half of the rule is the one that matters: a FILTER must never be
 * remembered this way. A filter left over from last week silently shortens a
 * queue; a hidden column visibly does not. The `Columns` menu always shows
 * every column, so a hidden one is one click from coming back.
 *
 * `defaults` gives a per-table starting set — P31 uses it to hide Doc Type,
 * Priority and Due on `/review` only, without touching `/tickets`.
 */
import { useCallback, useEffect, useState } from 'react';

function load(storageKey: string, known: Set<string>, defaults: string[]): string[] {
    if (typeof window === 'undefined') return defaults.filter(k => known.has(k));
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (raw === null) return defaults.filter(k => known.has(k));
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return defaults.filter(k => known.has(k));
        // Unknown keys are dropped rather than kept: a column renamed in a later
        // release would otherwise stay invisibly hidden forever.
        return parsed.filter((k): k is string => typeof k === 'string' && known.has(k));
    } catch {
        return defaults.filter(k => known.has(k));
    }
}

export function useHiddenColumns(allKeys: string[], storageKey: string, defaults: string[] = []) {
    const [hidden, setHidden] = useState<string[]>(() => load(storageKey, new Set(allKeys), defaults));

    useEffect(() => {
        if (typeof window === 'undefined') return;
        try { window.localStorage.setItem(storageKey, JSON.stringify(hidden)); } catch { /* quota */ }
    }, [hidden, storageKey]);

    const toggle = useCallback((key: string) => {
        setHidden(h => (h.includes(key) ? h.filter(k => k !== key) : [...h, key]));
    }, []);

    const showAll = useCallback(() => setHidden([]), []);

    const isHidden = useCallback((key: string) => hidden.includes(key), [hidden]);

    return { hidden, isHidden, toggle, showAll };
}
